/**
 * @module utils/package
 * @description Asset package utility functions for determining package status, expiry status,
 * and status display tones based on transfer state and expiration dates.
 */

import { DateTime } from 'luxon';
import { Assets } from 'src/types/assets';
import { Sanity } from 'src/types/sanity';
import { getDateDiff, hasExpired, hasLinkExpired, hasPackageExpired } from './date';

/**
 * @function getPackageStatus
 * @description Determine the display status of a package based on transfer status and expiry state
 * @param {Assets.Types.TransferStatus} transferStatus - Current transfer status from Aspera/MediaShuttle
 * @param {Assets.Types.ExpiryStatusProps} [expiryStatus] - Optional expiry status information
 * @returns {Assets.Types.PackageStatus} Computed package status for UI display
 */
export const getPackageStatus = (transferStatus: Assets.Types.TransferStatus, expiryStatus?: Assets.Types.ExpiryStatusProps): Assets.Types.PackageStatus => {
  if (transferStatus === Assets.Types.TransferStatus.STARTING || transferStatus === Assets.Types.TransferStatus.WAITING) {
    return Assets.Types.PackageStatus.STARTING;
  }
  if (expiryStatus?.isAnyExpired) {
    return Assets.Types.PackageStatus.EXPIRED;
  }
  if (transferStatus === Assets.Types.TransferStatus.RUNNING || transferStatus === Assets.Types.TransferStatus.PAUSED) {
    return Assets.Types.PackageStatus.PROCESSING;
  }

  if (transferStatus === Assets.Types.TransferStatus.FAILED) {
    return Assets.Types.PackageStatus.FAILED;
  }

  if (transferStatus === Assets.Types.TransferStatus.PARTIALLY_COMPLETED && expiryStatus?.isLinkExpired) {
    return Assets.Types.PackageStatus.WARNING;
  }

  return Assets.Types.PackageStatus.ACTIVE;
};

/**
 * @function getExpiryStatus
 * @description Calculate comprehensive expiry status for an asset package including
 * package expiry, link expiry, warning state, and status text.
 * @param {Sanity.Types.OrderAssetPackage} assetPackage - Asset package to evaluate
 * @returns {Assets.Types.ExpiryStatusProps} Expiry status properties including flags and display text
 */
export const getExpiryStatus = (assetPackage: Sanity.Types.OrderAssetPackage): Assets.Types.ExpiryStatusProps => {
  const isPackageExpired = hasPackageExpired(assetPackage);
  const isLinkExpired = hasLinkExpired(assetPackage);
  const isAnyExpired = hasExpired(assetPackage);
  let warning = false;

  const diff = getDateDiff(assetPackage.expiry || assetPackage.linkExpiry!);

  let statusText = 'Active';

  if (diff && diff.days <= Assets.Data.ExpiryThreshold[Assets.Types.ExpiryStatus.CRITICAL]) {
    statusText = 'Expires Soon';
    warning = true;
  }

  if (isAnyExpired) {
    warning = true;
    if (isPackageExpired && isLinkExpired) {
      statusText = 'Fully Expired';
    } else if (isPackageExpired) {
      statusText = 'Package Expired';
    } else {
      statusText = 'Link Expired';
    }
  }

  return {
    isPackageExpired,
    isLinkExpired,
    isAnyExpired,
    warning,
    statusText,
  };
};

/**
 * @function getExpiryTone
 * @description Determine the display tone/severity level for an asset package based on expiry state.
 * Uses thresholds from Assets.Data.ExpiryThreshold to determine warning levels.
 * @param {Sanity.Types.OrderAssetPackage} assetPackage - Asset package to evaluate
 * @returns {Sanity.Types.SanityTone} Tone level (DEFAULT, POSITIVE, CAUTION, or CRITICAL)
 */
export const getExpiryTone = (assetPackage: Sanity.Types.OrderAssetPackage): Sanity.Types.SanityTone => {
  if (hasExpired(assetPackage)) {
    return Sanity.Types.SanityTone.CRITICAL;
  }

  // Use the existing formatExpiry function to get the earliest expiry date
  const earliestExpiryDate = assetPackage.expiry && assetPackage.linkExpiry
    ? (DateTime.fromISO(assetPackage.expiry.toString()) < DateTime.fromISO(assetPackage.linkExpiry.toString())
      ? assetPackage.expiry
      : assetPackage.linkExpiry)
    : (assetPackage.expiry || assetPackage.linkExpiry);

  if (!earliestExpiryDate) {
    return Sanity.Types.SanityTone.DEFAULT;
  }

  const diff = getDateDiff(earliestExpiryDate);
  if (!diff) {
    return Sanity.Types.SanityTone.DEFAULT;
  }

  const daysUntilExpiry = Math.floor(diff.days);

  if (daysUntilExpiry <= Assets.Data.ExpiryThreshold[Assets.Types.ExpiryStatus.CRITICAL]) {
    return Sanity.Types.SanityTone.CRITICAL;
  }

  if (daysUntilExpiry <= Assets.Data.ExpiryThreshold[Assets.Types.ExpiryStatus.WARNING]) {
    return Sanity.Types.SanityTone.CAUTION;
  }

  if (daysUntilExpiry <= Assets.Data.ExpiryThreshold[Assets.Types.ExpiryStatus.NORMAL]) {
    return Sanity.Types.SanityTone.POSITIVE;
  }

  return Sanity.Types.SanityTone.DEFAULT;
};

/**
 * @function getStatusTone
 * @description Determine the overall display tone for a package considering both
 * transfer status and expiry state. Combines package status and expiry tone.
 * @param {Sanity.Types.OrderAssetPackage} assetPackage - Asset package to evaluate
 * @returns {Sanity.Types.SanityTone} Overall tone level for UI display
 */
export const getStatusTone = (assetPackage: Sanity.Types.OrderAssetPackage): Sanity.Types.SanityTone => {
  const expiryTone = getExpiryTone(assetPackage);
  const expiryStatus = getExpiryStatus(assetPackage);
  const packageStatus = getPackageStatus(assetPackage.transferStatus!, expiryStatus);

  // console.warn('** status', {
  //   expiryTone,
  //   expiryStatus,
  //   packageStatus,
  // })

  if (packageStatus === Assets.Types.PackageStatus.PROCESSING) {
    return Sanity.Types.SanityTone.CAUTION;
  }

  if (packageStatus === Assets.Types.PackageStatus.STARTING || (!expiryStatus.isAnyExpired && expiryTone === Sanity.Types.SanityTone.CRITICAL && packageStatus !== Assets.Types.PackageStatus.FAILED)) {
    return Sanity.Types.SanityTone.CAUTION;
  }

  if (packageStatus === Assets.Types.PackageStatus.FAILED) {
    return Sanity.Types.SanityTone.CRITICAL;
  }

  if (packageStatus === Assets.Types.PackageStatus.WARNING) {
    return Sanity.Types.SanityTone.CAUTION;
  }

  return expiryTone;
};
